/**
 * @file RepOrderRequestWidget.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description "Request an order" — the first thing on a rep's dashboard
 *              (Cam, 29 Oct 2026: "a new widget at the top (in mobile view)
 *              which allows them to send an order request/ standing order
 *              request"). The office sees it at the top of Sales Orders ("Rep
 *              requests") and makes the order from it.
 *
 *   Phone (the stack): the form inline — customer first, the rest once one
 *   is chosen — and "Your requests" under it, each with where it stands in
 *   words (Sent · Opened by Jade Games at 14:02 · Done — SO-D10871 · Closed:
 *   <reason>) and Withdraw while the office has not opened it.
 *   Laptop (a strip above the grid, config/repDashboardLayout top_strip): a
 *   bar with "New request" (the form in a window) and "Your requests".
 *
 *   Before migrations/rep_order_requests.sql runs it says "Not ready" and
 *   sends nothing.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { showAlert } from '../components/Alert';
import RepOrderRequestForm from '../components/RepOrderRequestForm';
import RepOrderRequestCard from '../components/RepOrderRequestCard';
import { useSlideOutButton } from '../components/SlideOutButton';
import { canWithdraw, isOpenStatus, NOT_READY_TEXT } from '../utils/repOrderRequests';
import { repRequestsApi } from '../utils/repOrderRequestApi';
import './RepWidgets.css';
import '../components/RepOrderRequests.css';

/** How many of "Your requests" show before "Show all". */
const SHOWN = 4;

/** "Your requests": each card, Withdraw while not yet opened. */
export function RepRequestsList({ requests, onChanged, limit = SHOWN, emptyText = 'Nothing sent yet.' }) {
  const [all, setAll] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const shown = all ? requests : requests.slice(0, limit);

  const withdraw = async (r) => {
    setConfirm(null);
    try {
      await repRequestsApi.withdraw(r.id);
      showAlert('Withdrawn — the office will not see it now', 'success');
      if (onChanged) onChanged();
    } catch (e) {
      showAlert(e.message, 'error');
      if (onChanged) onChanged();
    }
  };

  if (!requests.length) return <div className="ror-empty">{emptyText}</div>;
  return (
    <div className="ror-list">
      {shown.map(r => (
        <RepOrderRequestCard
          key={r.id}
          request={r}
          actions={canWithdraw(r) ? (
            <button type="button" className="ror-action" data-read-safe onClick={() => setConfirm(r)}>
              <span className="material-symbols-rounded" aria-hidden="true">undo</span>
              Withdraw
            </button>
          ) : null}
        />
      ))}
      {requests.length > limit && (
        <button type="button" className="ror-link ror-show-all" onClick={() => setAll(v => !v)}>
          {all ? 'Show fewer' : `Show all ${requests.length}`}
        </button>
      )}
      <ConfirmDialog
        isOpen={!!confirm}
        title="Withdraw this request?"
        message="The office has not opened it yet. Withdrawn, it leaves their list — send a new one if you still need it."
        confirmText="Withdraw"
        cancelText="Keep it"
        confirmIcon="undo"
        onConfirm={() => withdraw(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

/** The form's data and the rep's requests, loaded together. */
export function useRepRequestData() {
  const [form, setForm] = useState({ loaded: false, ready: true, accounts: [], additives: [], error: null });
  const [mine, setMine] = useState([]);

  const loadMine = useCallback(() => {
    repRequestsApi.mine().then(d => setMine(d.requests || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    repRequestsApi.form()
      .then(d => { if (!cancelled) setForm({ loaded: true, ready: d.ready !== false, accounts: d.accounts || [], additives: d.additives || [], error: null }); })
      .catch(e => { if (!cancelled) setForm({ loaded: true, ready: true, accounts: [], additives: [], error: e.message }); });
    loadMine();
    return () => { cancelled = true; };
  }, [loadMine]);

  return { form, mine, loadMine };
}

function RepOrderRequestWidget({ variant = 'desktop' }) {
  const phone = variant === 'phone';
  const { form, mine, loadMine } = useRepRequestData();
  const [showForm, setShowForm] = useState(false);
  const [showList, setShowList] = useState(false);
  const withOffice = mine.filter(r => isOpenStatus(r.status)).length;
  useSlideOutButton([phone, withOffice, form.loaded]);

  const notReady = form.loaded && !form.ready;

  if (phone) {
    return (
      <div className="dashboard-card rep-widget rep-order-request-card">
        <div className="card-header">
          <h3>
            <span className="material-symbols-rounded">add_shopping_cart</span>
            Request an order
          </h3>
        </div>
        <div className="card-body">
          {form.error && <div className="ror-error">{form.error}</div>}
          {!form.loaded && <div className="rep-widget-state">Loading…</div>}
          {form.loaded && !form.error && (
            <RepOrderRequestForm
              inline
              accounts={form.accounts}
              additives={form.additives}
              ready={!notReady}
              notReadyText={NOT_READY_TEXT}
              onSent={loadMine}
            />
          )}
          {!notReady && (
            <div className="ror-yours">
              <h4 className="ror-yours-title">Your requests</h4>
              <RepRequestsList requests={mine} onChanged={loadMine} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card rep-widget rep-order-request-strip">
      <div className="ror-strip-title">
        <span className="material-symbols-rounded" aria-hidden="true">add_shopping_cart</span>
        <h3>Request an order</h3>
      </div>
      <span className="ror-strip-text">
        {notReady ? NOT_READY_TEXT
          : 'Ask the office for an order, a standing order or a change to one — they make it from your request.'}
      </span>
      <div className="buttons slideout ror-strip-buttons">
        <div>
          <button type="button" className="btn-slideout" data-read-safe onClick={() => setShowList(true)} disabled={notReady}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">list_alt</span></div>
            <span className="btn-label">Your requests{withOffice ? ` (${withOffice} with the office)` : ''}</span>
          </button>
        </div>
        <div>
          <button type="button" className="btn-slideout" data-read-safe onClick={() => setShowForm(true)} disabled={notReady || !form.loaded}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
            <span className="btn-label">New request</span>
          </button>
        </div>
      </div>

      {showForm && (
        <Modal isOpen onClose={() => setShowForm(false)} title="Request an order" size="md" className="rep-order-request-modal">
          <RepOrderRequestForm
            accounts={form.accounts}
            additives={form.additives}
            ready={!notReady}
            notReadyText={NOT_READY_TEXT}
            onSent={() => { loadMine(); }}
          />
        </Modal>
      )}
      {showList && (
        <Modal isOpen onClose={() => setShowList(false)} title="Your requests" size="md" className="rep-order-request-modal">
          <RepRequestsList requests={mine} onChanged={loadMine} limit={50} />
        </Modal>
      )}
    </div>
  );
}

export default RepOrderRequestWidget;
